import { getFilePreviewKey } from "~/common/utils";
import { getConfig } from "~/config/configProvider";
import type { WrappedServiceModule } from "~/hooks/useServices";
import type * as s3Service from "~/services/s3";

const config = getConfig();

// Most-recently-opened sub-rosa videos: keep at most this many signed URLs
// in memory. Returning the same signed URL across re-opens lets the
// browser's HTTP cache serve the video bytes (signed URLs vary per call,
// so a fresh URL would bypass the cache entirely). Capacity is kept small
// because the typical interaction is "open one entry's video, close, open
// the next" — paying one re-sign on a back-and-forth past 2 files is
// cheaper than holding many stale URLs across the session.
const LRU_CAPACITY = 2;
// Sign URLs for 1 hour…
const URL_TTL_SEC = 3600;
// …but refresh ~5 min before the deadline so timer skew + a re-open near
// the boundary don't return a URL that 403s mid-playback.
const EXPIRY_BUFFER_MS = 5 * 60 * 1000;

interface CacheEntry {
  url: string;
  expiresAt: number;
}

// Map insertion order is LRU order: delete+set re-promotes an entry.
const signedUrlCache = new Map<string, CacheEntry>();

const promote = (fileId: string, entry: CacheEntry) => {
  signedUrlCache.delete(fileId);
  signedUrlCache.set(fileId, entry);
  while (signedUrlCache.size > LRU_CAPACITY) {
    const oldest = signedUrlCache.keys().next().value;
    if (oldest === undefined) break;
    signedUrlCache.delete(oldest);
  }
};

/**
 * Return a signed preview URL for a sub-rosa upload, hitting the
 * LRU cache when possible. Resolves `Document.preview` (compressed mp4
 * from PR #6623) and falls back to the original key when no preview is
 * attached yet.
 */
export const getSignedPreviewUrl = async (
  s3: WrappedServiceModule<typeof s3Service>,
  file: { id: string; key: string },
): Promise<string> => {
  const cached = signedUrlCache.get(file.id);
  if (cached && cached.expiresAt - EXPIRY_BUFFER_MS > Date.now()) {
    promote(file.id, cached);
    return cached.url;
  }

  const previewKey = await getFilePreviewKey({ id: file.id, key: file.key });
  const result = await s3.getDownloadUrl(
    config.RECORDS_BUCKET_NAME,
    previewKey,
    { expiresIn: URL_TTL_SEC },
  );
  if (result.error) throw new Error("Failed to sign video URL");
  if (!result.data) throw new Error("No signed URL returned");

  const entry: CacheEntry = {
    url: result.data,
    expiresAt: Date.now() + URL_TTL_SEC * 1000,
  };
  promote(file.id, entry);
  return entry.url;
};

/** Test-only helper to clear the signed-URL cache between tests. */
export const __resetVideoPreviewCacheForTests = () => {
  signedUrlCache.clear();
};
